import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import {
  collection,
  doc,
  onSnapshot,
  addDoc,
  updateDoc,
  setDoc,
  serverTimestamp,
  query,
  orderBy,
  where,
  getDocs,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/firebase'
import { useSyncStore } from './sync'
import { useAuthStore } from './auth'
import { writeAuditLog } from '@/services/audit'
import type { Personnel } from '@/types'
import { PersonnelStatus, TeamType, OverheadRole } from '@/types'

// Grant the current user cross-agency access to this incident by writing the
// incident_members flat index. The Firestore rule allows a signed-in user to
// create their own membership doc (request.auth.uid == uid) with no incident
// read, so we write it directly — reading the incident first would itself be
// permission-denied for a user who isn't a member yet.
async function ensureIncidentMember(incidentId: string, userId: string): Promise<void> {
  await setDoc(
    doc(db, 'incidents', incidentId, 'incident_members', userId),
    { added_at: serverTimestamp() },
    { merge: true },
  )
}

function personnelCol(incidentId: string, opId: string) {
  return collection(db, 'incidents', incidentId, 'operational_periods', opId, 'personnel')
}

function personnelDoc(incidentId: string, opId: string, personnelId: string) {
  return doc(db, 'incidents', incidentId, 'operational_periods', opId, 'personnel', personnelId)
}

export const usePersonnelStore = defineStore('personnel', () => {
  const authStore = useAuthStore()
  const roster = ref<Personnel[]>([])
  const loading = ref(false)

  let unsubscribe: Unsubscribe | null = null
  let _incidentId = ''
  let _opId = ''

  const onSceneCount = computed(() => roster.value.filter((p) => p.time_out === null).length)
  const checkedOutCount = computed(() => roster.value.filter((p) => p.time_out !== null).length)
  const myRecord = computed(
    () =>
      roster.value.find(
        (p) => p.user_id === authStore.currentUser?.uid && p.time_out === null,
      ) ?? null,
  )

  function subscribeToPersonnel(incidentId: string, opId: string) {
    if (incidentId === _incidentId && opId === _opId && unsubscribe) return
    if (unsubscribe) unsubscribe()
    _incidentId = incidentId
    _opId = opId
    loading.value = true
    const q = query(personnelCol(incidentId, opId), orderBy('time_in', 'desc'))
    unsubscribe = onSnapshot(q, (snap) => {
      roster.value = snap.docs.map((d) => ({ personnel_id: d.id, ...d.data() } as Personnel))
      loading.value = false
    }, () => {
      // permission-denied (e.g. QR walk-in user not yet in incident_members) — treat as empty roster
      loading.value = false
    })
  }

  async function checkIn(
    incidentId: string,
    opId: string,
    data: Omit<Personnel, 'personnel_id' | 'incident_id' | 'op_id' | 'time_in' | 'time_out' | 'status'>,
    joinAs: 'field' | 'overhead' = 'field',
    overheadRole?: OverheadRole,
  ): Promise<string> {
    const syncStore = useSyncStore()

    // Grant cross-agency access up front. Every read/write below (teams,
    // user_ops, audit_log) is gated by canAccessIncident, which becomes true
    // once this membership doc exists — so it must be written first.
    // Only for self check-ins: the rule rejects writing another user's
    // membership doc, which an IC checking in a linked roster member would do.
    if (data.user_id && data.user_id === authStore.currentUser?.uid) {
      await ensureIncidentMember(incidentId, data.user_id)
    }

    const qualifications = data.qualifications ?? {}

    let status = PersonnelStatus.Available
    let teamId: string | null = null
    let category = data.category || ''

    if (joinAs === 'overhead') {
      // Find the Overhead team for this OP
      const teamsCol = collection(db, 'incidents', incidentId, 'operational_periods', opId, 'teams')
      const overheadSnap = await getDocs(query(teamsCol, where('team_type', '==', TeamType.Overhead)))
      const overheadDoc = overheadSnap.docs[0]
      if (!overheadSnap.empty && overheadDoc) {
        teamId = overheadDoc.id
        status = PersonnelStatus.Assigned
        category = overheadRole ?? ''
      }

      // Displace anyone currently holding the same overhead role
      if (overheadRole) {
        const userOpsCol = collection(db, 'incidents', incidentId, 'operational_periods', opId, 'user_ops')
        const [existingPersonnelSnap, existingUserOpSnap] = await Promise.all([
          getDocs(query(personnelCol(incidentId, opId), where('category', '==', overheadRole), where('time_out', '==', null))),
          getDocs(query(userOpsCol, where('role', '==', overheadRole), where('active', '==', true))),
        ])
        await Promise.all([
          ...existingPersonnelSnap.docs.map((d) => updateDoc(d.ref, { category: OverheadRole.GeneralOverhead })),
          ...existingUserOpSnap.docs.map((d) => updateDoc(d.ref, { role: OverheadRole.GeneralOverhead })),
        ])
      }
    }

    const payload = {
      ...data,
      incident_id: incidentId,
      op_id: opId,
      qualifications,
      category,
      team_id: teamId,
      time_in: serverTimestamp(),
      time_out: null,
      status,
    }

    syncStore.pendingWrites++
    try {
      const docRef = await addDoc(personnelCol(incidentId, opId), payload)

      // Create a user_op record:
      // - Always for overhead check-ins so the role is persisted in user_ops (even for walk-ins)
      // - For field check-ins, only when user_id is known (provides role context for useCurrentOpRole)
      if (joinAs === 'overhead' || data.user_id) {
        const userOpsCol = collection(db, 'incidents', incidentId, 'operational_periods', opId, 'user_ops')
        // Deactivate existing active user_op records for this person
        if (data.user_id) {
          const activeSnap = await getDocs(
            query(userOpsCol, where('user_id', '==', data.user_id), where('active', '==', true)),
          )
          for (const d of activeSnap.docs) {
            await updateDoc(d.ref, { active: false })
          }
        }
        // Create new active user_op — overhead gets their specific role; field units default to Team Member
        await addDoc(userOpsCol, {
          user_id: data.user_id ?? null,
          personnel_id: docRef.id,
          op_id: opId,
          team_id: joinAs === 'overhead' ? teamId : null,
          active: true,
          role: joinAs === 'overhead' ? (overheadRole ?? '') : 'Team Member',
          created_at: serverTimestamp(),
        })
      }

      await writeAuditLog(incidentId, 'personnel.checked_in', 'personnel', docRef.id, null, payload)
      return docRef.id
    } finally {
      syncStore.pendingWrites--
    }
  }

  async function checkOut(incidentId: string, opId: string, personnelId: string): Promise<void> {
    const syncStore = useSyncStore()
    const patch = { time_out: serverTimestamp(), status: PersonnelStatus.OutOfService }
    syncStore.pendingWrites++
    try {
      await updateDoc(personnelDoc(incidentId, opId, personnelId), patch)
      await writeAuditLog(incidentId, 'personnel.checked_out', 'personnel', personnelId, null, patch)
    } finally {
      syncStore.pendingWrites--
    }
  }

  async function updateStatus(
    incidentId: string,
    opId: string,
    personnelId: string,
    status: PersonnelStatus,
  ): Promise<void> {
    const syncStore = useSyncStore()
    const patch = { status }
    syncStore.pendingWrites++
    try {
      await updateDoc(personnelDoc(incidentId, opId, personnelId), patch)
      await writeAuditLog(incidentId, 'personnel.status_changed', 'personnel', personnelId, null, patch)
    } finally {
      syncStore.pendingWrites--
    }
  }

  function teardown() {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
    _incidentId = ''
    _opId = ''
    roster.value = []
    loading.value = false
  }

  return {
    roster,
    loading,
    onSceneCount,
    checkedOutCount,
    myRecord,
    subscribeToPersonnel,
    checkIn,
    checkOut,
    updateStatus,
    teardown,
  }
})
